import { useTranslation } from 'react-i18next';
import { Building2, Mail, Phone, MapPin, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const Footer = ({ onContactClick, onLegalClick, onPrivacyClick }) => {
    const { t } = useTranslation();
    const currentYear = new Date().getFullYear();

    return (
        <footer className="bg-foreground text-background">
            {/* Partner Banks Section Header */}
            <div className="bg-background">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="text-center">
                        <span className="text-accent font-medium text-sm uppercase tracking-wider">
                            {t('footer.trustedPartners')}
                        </span>
                    </div>
                </div>
            </div>

            {/* Main Footer Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
                    {/* Company Info */}
                    <div className="lg:col-span-2">
                        <a href="/" className="flex items-center gap-3 mb-6">
                            <div className="relative">
                                <img 
                                    src="https://customer-assets.emergentagent.com/job_modern-spilka-1/artifacts/xb96a3rm_imagen.png" 
                                    alt="Spilka Consulting Logo"
                                    className="w-10 h-10 object-contain"
                                    style={{ filter: 'hue-rotate(120deg) saturate(1.2)' }}
                                />
                            </div>
                            <span className="font-display text-xl font-semibold text-background tracking-tight">
                                SPILKA CONSULTING
                            </span>
                        </a>
                        <p className="text-background/70 leading-relaxed max-w-md mb-6">
                            {t('footer.companyDescription')}
                        </p>
                        <a
                            href="https://www.casaconsulting.ch"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <Button className="btn-gold rounded-full px-6">
                                <Building2 className="w-4 h-4 mr-2" />
                                {t('nav.realEstate')}
                                <ExternalLink className="w-3 h-3 ml-2" />
                            </Button>
                        </a>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h4 className="font-display text-lg font-semibold mb-6 text-background">
                            {t('footer.quickLinks')}
                        </h4>
                        <ul className="space-y-3">
                            <li>
                                <a
                                    href="#simulator"
                                    className="text-background/70 hover:text-accent transition-colors duration-200"
                                >
                                    {t('nav.loanSimulator')}
                                </a>
                            </li>
                            <li>
                                <a
                                    href="#about"
                                    className="text-background/70 hover:text-accent transition-colors duration-200"
                                >
                                    {t('nav.aboutUs')}
                                </a>
                            </li>
                            <li>
                                <button
                                    onClick={onContactClick}
                                    className="text-background/70 hover:text-accent transition-colors duration-200 cursor-pointer"
                                >
                                    {t('nav.contact')}
                                </button>
                            </li>
                            <li>
                                <button
                                    onClick={onLegalClick}
                                    className="text-background/70 hover:text-accent transition-colors duration-200 cursor-pointer"
                                >
                                    {t('footer.legalNotice')}
                                </button>
                            </li>
                            <li>
                                <button
                                    onClick={onPrivacyClick}
                                    className="text-background/70 hover:text-accent transition-colors duration-200 cursor-pointer"
                                >
                                    {t('footer.privacy')}
                                </button>
                            </li>
                        </ul>
                    </div>

                    {/* Contact Info */}
                    <div id="contact">
                        <h4 className="font-display text-lg font-semibold mb-6 text-background">
                            {t('footer.contactUs')}
                        </h4>
                        <ul className="space-y-4">
                            <li className="flex items-start gap-3">
                                <MapPin className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                                <span className="text-background/70">
                                    {t('footer.switzerland')}
                                </span>
                            </li>
                            <li className="flex items-center gap-3">
                                <Mail className="w-5 h-5 text-accent flex-shrink-0" />
                                <a
                                    href="mailto:info@spilkaconsulting.com"
                                    className="text-background/70 hover:text-accent transition-colors duration-200"
                                >
                                    info@spilkaconsulting.com
                                </a>
                            </li>
                            <li className="flex items-center gap-3">
                                <Phone className="w-5 h-5 text-accent flex-shrink-0" />
                                <button
                                    onClick={onContactClick}
                                    className="text-background/70 hover:text-accent transition-colors duration-200 cursor-pointer"
                                >
                                    {t('footer.contactUs')}
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Bottom Bar */}
            <div className="border-t border-background/10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                        <p className="text-background/50 text-sm">
                            {t('footer.copyright', { year: currentYear })}
                        </p>
                        <div className="flex items-center gap-6">
                            <button
                                onClick={onLegalClick}
                                className="text-background/50 text-sm hover:text-accent transition-colors duration-200 cursor-pointer"
                            >
                                {t('footer.legalNotice')}
                            </button>
                            <button
                                onClick={onPrivacyClick}
                                className="text-background/50 text-sm hover:text-accent transition-colors duration-200 cursor-pointer"
                            >
                                {t('footer.privacy')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};
